import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { format } from 'date-fns'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ProviderChatThreadModel } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/messages/ProviderChatThreads'

import { useThreadPreviews } from './useThreadPreviews'
import { useGuardianForSelectedClient } from '../../../../hooks/useGuardianForSelectedClient'
import { useSelectedClientData } from '../../../clients/clientDetails/data/hooks/useSelectedClientData'
import { MessengerParticipant, MessengerThread } from '../types'

export const useProviderChatThreadRows = (threads: MessengerThread[]): ProviderChatThreadModel[] => {
  const { formatMessage } = useIntl()
  const { isPreferredNameEnabled } = useFlags()
  const { data: client } = useSelectedClientData()
  const { guardian } = useGuardianForSelectedClient()
  const previews = useThreadPreviews(threads)

  const patientFirstName =
    (isPreferredNameEnabled ? (client?.preferred_first_name ?? client?.first_name) : client?.first_name) ?? ''
  const guardianFirstName = guardian.guardianFirstName ?? ''

  const teenRole = formatMessage({
    defaultMessage: 'Teen',
    description: 'Popover thread row role label for the teen client',
  })
  const caregiverRole = formatMessage({
    defaultMessage: 'Caregiver',
    description: 'Popover thread row role label for the caregiver',
  })

  return useMemo(
    () =>
      threads.map((thread) => {
        const isGuardian = thread.participant === MessengerParticipant.GUARDIAN
        const firstName = isGuardian ? guardianFirstName : patientFirstName
        const role = isGuardian ? caregiverRole : teenRole
        const sentAt = thread.conversation?.conversation_last_message_sent_datetime
        return {
          id: thread.id,
          name: `${firstName} • ${role}`,
          date: sentAt ? format(new Date(sentAt), 'MMM d') : '',
          messagePreview: previews[thread.participant] ?? '',
          hasUnreadMessage: thread.unreadCount > 0,
        }
      }),
    [threads, previews, patientFirstName, guardianFirstName, teenRole, caregiverRole],
  )
}
